/**
 * Scheduling React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SchedulingService } from '../api/services/schedulingService';
import type {
  Zone,
  Segment,
  Sequence,
  Playlist,
  Schedule,
  ScheduleEntry,
} from '../api/types';

// Query keys
export const schedulingKeys = {
  all: ['scheduling'] as const,
  dashboard: () => [...schedulingKeys.all, 'dashboard'] as const,
  nowPlaying: (params?: any) => [...schedulingKeys.dashboard(), 'now-playing', params] as const,

  zones: {
    all: ['zones'] as const,
    list: (params?: any) => [...schedulingKeys.zones.all, 'list', params] as const,
    detail: (id: string) => [...schedulingKeys.zones.all, id] as const,
    nowPlaying: (id: string) => [...schedulingKeys.zones.all, id, 'now-playing'] as const,
    week: (id: string) => [...schedulingKeys.zones.all, id, 'week'] as const,
  },

  segments: {
    all: ['segments'] as const,
    list: (params?: any) => [...schedulingKeys.segments.all, 'list', params] as const,
    detail: (id: string) => [...schedulingKeys.segments.all, id] as const,
    analysis: (id: string) => [...schedulingKeys.segments.all, id, 'analysis'] as const,
  },

  sequences: {
    all: ['sequences'] as const,
    list: (params?: any) => [...schedulingKeys.sequences.all, 'list', params] as const,
    detail: (id: string) => [...schedulingKeys.sequences.all, id] as const,
    preview: (id: string) => [...schedulingKeys.sequences.all, id, 'preview'] as const,
  },

  playlists: {
    all: ['scheduling-playlists'] as const,
    list: (params?: any) => [...schedulingKeys.playlists.all, 'list', params] as const,
    detail: (id: string) => [...schedulingKeys.playlists.all, id] as const,
  },

  schedules: {
    all: ['schedules'] as const,
    list: (params?: any) => [...schedulingKeys.schedules.all, 'list', params] as const,
    detail: (id: string) => [...schedulingKeys.schedules.all, id] as const,
    gaps: (id: string) => [...schedulingKeys.schedules.all, id, 'gaps'] as const,
  },

  energyCurves: (orgId?: string) => [...schedulingKeys.all, 'energy-curves', orgId] as const,
  dayparts: (orgId?: string) => [...schedulingKeys.all, 'dayparts', orgId] as const,
};

// =============================================================================
// Dashboard Hooks
// =============================================================================

export function useSchedulingDashboard(params?: { organization_id?: string; site_id?: string }) {
  return useQuery({
    queryKey: schedulingKeys.nowPlaying(params),
    queryFn: () => SchedulingService.getDashboardNowPlaying(params),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refresh every minute
  });
}

export function useZoneWeekSchedule(zoneId: string) {
  return useQuery({
    queryKey: schedulingKeys.zones.week(zoneId),
    queryFn: () => SchedulingService.getZoneWeekSchedule(zoneId),
    enabled: !!zoneId,
  });
}

// =============================================================================
// Zone Hooks
// =============================================================================

export function useZones(params?: { organization_id?: string; site_id?: string; is_active?: boolean }) {
  return useQuery({
    queryKey: schedulingKeys.zones.list(params),
    queryFn: () => SchedulingService.listZones(params),
  });
}

export function useZone(id: string) {
  return useQuery({
    queryKey: schedulingKeys.zones.detail(id),
    queryFn: () => SchedulingService.getZone(id),
    enabled: !!id,
  });
}

export function useZoneNowPlaying(id: string) {
  return useQuery({
    queryKey: schedulingKeys.zones.nowPlaying(id),
    queryFn: () => SchedulingService.getZoneNowPlaying(id),
    enabled: !!id,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

export function useCreateZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Zone>) => SchedulingService.createZone(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.zones.all });
    },
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Zone> }) =>
      SchedulingService.updateZone(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.zones.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.zones.detail(id) });
    },
  });
}

export function useDeleteZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SchedulingService.deleteZone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.zones.all });
    },
  });
}

export function useDuplicateZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ zoneId, name, targetSiteId }: { zoneId: string; name?: string; targetSiteId?: string }) =>
      SchedulingService.duplicateZone(zoneId, name, targetSiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.zones.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.all });
    },
  });
}

// =============================================================================
// Segment Hooks
// =============================================================================

export function useSegments(params?: { organization_id?: string; search?: string }) {
  return useQuery({
    queryKey: schedulingKeys.segments.list(params),
    queryFn: () => SchedulingService.listSegments(params),
  });
}

export function useSegment(id: string) {
  return useQuery({
    queryKey: schedulingKeys.segments.detail(id),
    queryFn: () => SchedulingService.getSegment(id),
    enabled: !!id,
  });
}

export function useSegmentAnalysis(id: string) {
  return useQuery({
    queryKey: schedulingKeys.segments.analysis(id),
    queryFn: () => SchedulingService.analyzeSegment(id),
    enabled: !!id,
  });
}

export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Segment>) => SchedulingService.createSegment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.all });
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Segment> }) =>
      SchedulingService.updateSegment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.detail(id) });
    },
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SchedulingService.deleteSegment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.all });
    },
  });
}

export function useAddTrackToSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      contentId,
      position,
      transitionType,
    }: {
      segmentId: string;
      contentId: string;
      position?: number;
      transitionType?: string;
    }) => SchedulingService.addTrackToSegment(segmentId, contentId, position, transitionType),
    onSuccess: (_, { segmentId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.detail(segmentId) });
    },
  });
}

export function useRemoveTrackFromSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, trackId }: { segmentId: string; trackId: string }) =>
      SchedulingService.removeTrackFromSegment(segmentId, trackId),
    onSuccess: (_, { segmentId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.detail(segmentId) });
    },
  });
}

export function useReorderSegmentTracks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, itemIds }: { segmentId: string; itemIds: string[] }) =>
      SchedulingService.reorderSegmentTracks(segmentId, itemIds),
    onSuccess: (_, { segmentId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.detail(segmentId) });
    },
  });
}

export function useAutoFillSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, maxTracks }: { segmentId: string; maxTracks?: number }) =>
      SchedulingService.autoFillSegment(segmentId, maxTracks),
    onSuccess: (_, { segmentId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.detail(segmentId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.analysis(segmentId) });
    },
  });
}

export function useDuplicateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, name, targetOrganizationId }: { segmentId: string; name?: string; targetOrganizationId?: string }) =>
      SchedulingService.duplicateSegment(segmentId, name, targetOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.segments.all });
    },
  });
}

// =============================================================================
// Sequence Hooks
// =============================================================================

export function useSequences(params?: { organization_id?: string; search?: string }) {
  return useQuery({
    queryKey: schedulingKeys.sequences.list(params),
    queryFn: () => SchedulingService.listSequences(params),
  });
}

export function useSequence(id: string) {
  return useQuery({
    queryKey: schedulingKeys.sequences.detail(id),
    queryFn: () => SchedulingService.getSequence(id),
    enabled: !!id,
  });
}

export function useSequencePreview(id: string) {
  return useQuery({
    queryKey: schedulingKeys.sequences.preview(id),
    queryFn: () => SchedulingService.previewSequence(id),
    enabled: !!id,
  });
}

export function useCreateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Sequence>) => SchedulingService.createSequence(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.all });
    },
  });
}

export function useUpdateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Sequence> }) =>
      SchedulingService.updateSequence(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.detail(id) });
    },
  });
}

export function useDeleteSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SchedulingService.deleteSequence(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.all });
    },
  });
}

export function useAddSegmentToSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sequenceId,
      segmentId,
      position,
    }: {
      sequenceId: string;
      segmentId: string;
      position?: number;
    }) => SchedulingService.addSegmentToSequence(sequenceId, segmentId, position),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.detail(sequenceId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.preview(sequenceId) });
    },
  });
}

export function useRemoveSegmentFromSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, itemId }: { sequenceId: string; itemId: string }) =>
      SchedulingService.removeSegmentFromSequence(sequenceId, itemId),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.detail(sequenceId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.preview(sequenceId) });
    },
  });
}

export function useReorderSequenceSegments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, itemIds }: { sequenceId: string; itemIds: string[] }) =>
      SchedulingService.reorderSequenceSegments(sequenceId, itemIds),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.detail(sequenceId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.preview(sequenceId) });
    },
  });
}

export function useDuplicateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, name, targetOrganizationId }: { sequenceId: string; name?: string; targetOrganizationId?: string }) =>
      SchedulingService.duplicateSequence(sequenceId, name, targetOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.sequences.all });
    },
  });
}

// =============================================================================
// Playlist Hooks
// =============================================================================

export function useSchedulingPlaylists(params?: {
  organization_id?: string;
  is_active?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: schedulingKeys.playlists.list(params),
    queryFn: () => SchedulingService.listPlaylists(params),
  });
}

export function useSchedulingPlaylist(id: string) {
  return useQuery({
    queryKey: schedulingKeys.playlists.detail(id),
    queryFn: () => SchedulingService.getPlaylist(id),
    enabled: !!id,
  });
}

export function useCreateSchedulingPlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Playlist>) => SchedulingService.createPlaylist(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.all });
    },
  });
}

export function useUpdateSchedulingPlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Playlist> }) =>
      SchedulingService.updatePlaylist(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.detail(id) });
    },
  });
}

export function useDeleteSchedulingPlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SchedulingService.deletePlaylist(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.all });
    },
  });
}

export function useAddTrackToPlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      playlistId,
      contentId,
      position,
    }: {
      playlistId: string;
      contentId: string;
      position?: number;
    }) => SchedulingService.addTrackToPlaylist(playlistId, contentId, position),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.detail(playlistId) });
    },
  });
}

export function useRemoveTrackFromPlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playlistId, itemId }: { playlistId: string; itemId: string }) =>
      SchedulingService.removeTrackFromPlaylist(playlistId, itemId),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.detail(playlistId) });
    },
  });
}

export function useReorderPlaylistTracks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playlistId, itemIds }: { playlistId: string; itemIds: string[] }) =>
      SchedulingService.reorderPlaylistTracks(playlistId, itemIds),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.detail(playlistId) });
    },
  });
}

export function useDuplicatePlaylist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playlistId, name, targetOrganizationId }: { playlistId: string; name?: string; targetOrganizationId?: string }) =>
      SchedulingService.duplicatePlaylist(playlistId, name, targetOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.playlists.all });
    },
  });
}

// =============================================================================
// Schedule Hooks
// =============================================================================

export function useSchedules(params?: {
  organization_id?: string;
  zone_id?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: schedulingKeys.schedules.list(params),
    queryFn: () => SchedulingService.listSchedules(params),
  });
}

export function useSchedule(id: string) {
  return useQuery({
    queryKey: schedulingKeys.schedules.detail(id),
    queryFn: () => SchedulingService.getSchedule(id),
    enabled: !!id,
  });
}

export function useScheduleGaps(id: string) {
  return useQuery({
    queryKey: schedulingKeys.schedules.gaps(id),
    queryFn: () => SchedulingService.getScheduleGaps(id),
    enabled: !!id,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Schedule>) => SchedulingService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.all });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Schedule> }) =>
      SchedulingService.updateSchedule(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.all });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.detail(id) });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SchedulingService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.all });
    },
  });
}

export function useAddScheduleEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: string; data: Partial<ScheduleEntry> }) =>
      SchedulingService.addScheduleEntry(scheduleId, data),
    onSuccess: (_, { scheduleId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.detail(scheduleId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.gaps(scheduleId) });
    },
  });
}

export function useUpdateScheduleEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      entryId,
      data,
    }: {
      scheduleId: string;
      entryId: string;
      data: Partial<ScheduleEntry>;
    }) => SchedulingService.updateScheduleEntry(scheduleId, entryId, data),
    onSuccess: (_, { scheduleId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.detail(scheduleId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.gaps(scheduleId) });
    },
  });
}

export function useDeleteScheduleEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, entryId }: { scheduleId: string; entryId: string }) =>
      SchedulingService.deleteScheduleEntry(scheduleId, entryId),
    onSuccess: (_, { scheduleId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.detail(scheduleId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.gaps(scheduleId) });
    },
  });
}

export function useCloneScheduleDay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      sourceDay,
      targetDays,
    }: {
      scheduleId: string;
      sourceDay: number;
      targetDays: number[];
    }) => SchedulingService.cloneScheduleDay(scheduleId, sourceDay, targetDays),
    onSuccess: (_, { scheduleId }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.detail(scheduleId) });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules.gaps(scheduleId) });
    },
  });
}

// =============================================================================
// Energy Curves & Dayparts Hooks
// =============================================================================

export function useEnergyCurves(organizationId?: string) {
  return useQuery({
    queryKey: schedulingKeys.energyCurves(organizationId),
    queryFn: () => SchedulingService.listEnergyCurves(organizationId),
  });
}

export function useDayparts(organizationId?: string) {
  return useQuery({
    queryKey: schedulingKeys.dayparts(organizationId),
    queryFn: () => SchedulingService.listDayparts(organizationId),
  });
}
